import { trim } from '../utils/trim';

class MarkdownSerializer {
  constructor(nodes, marks) {
    this.nodes = nodes;
    this.marks = marks;
  }

  serialize(content) {
    return this.serializeFragment(content);
  }

  serializeTable(node) {
    let html = '<table' + this.serializeAttrs(node.attrs) + '>\n';
    node.content.forEach((row, _, rowIndex) => {
      if (rowIndex > 0 && row.type.name === 'tableRow') {
        html += this.serializeTableRow(row, rowIndex === 1);
      }
    });
    html += '</table>\n\n';
    return html;
  }

  serializeTableRow(node, headerCell = false) {
    let html = '<tr>\n';
    node.content.forEach((cell) => {
      // Only serialize cells that are not tableControllerCell
      if (cell.type.name !== 'tableControllerCell') {
        html += this.serializeTableCell(cell, headerCell);
      }
    });
    html += '</tr>\n';
    return html;
  }

  serializeTableCell(node, headerCell = false) {
    const tag = headerCell ? 'th' : 'td';
    const attrs = this.serializeAttrs(node.attrs);
    let html = `<${tag}${attrs}>\n`;
    html += this.serializeFragment(node.content);
    html += `</${tag}>\n`;
    return html;
  }

  serializeIframe(node) {
    const attrs = this.serializeAttrs(node.attrs);
    const iframe_html = `<iframe ${attrs}></iframe>`;
    return `<div style={${JSON.stringify({
      display: 'flex',
      justifyContent: 'center',
      alignItem: 'center',
      width: '100%',
      marginBottom: '2rem',
      overflow: 'hidden',
    })}}>${iframe_html}</div>\n\n`;
  }

  serializeAttrs(attrs) {
    if (!attrs) return '';

    const validAttrs = [
      'colspan',
      'rowspan',
      'style',
      'background',
      'src',
      'allowFullScreen',
      'frameBorder',
      'type',
      'width',
      'height',
    ];
    let attrString = '';

    Object.entries(attrs).forEach(([key, value]) => {
      if (validAttrs.includes(key) && value != null) {
        if (key === 'style') {
          attrString += ` style="${this.serializeStyle(value)}"`;
        } else {
          const html_attr = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          attrString += ` ${html_attr}="${value}"`;
        }
      }
    });

    return attrString;
  }

  serializeStyle(style) {
    if (typeof style === 'string') return style;
    if (typeof style === 'object') {
      return Object.entries(style)
        .map(([key, value]) => `${key}: ${value};`)
        .join(' ');
    }
    return '';
  }

  serializeFragment(fragment) {
    let result = '';
    fragment.forEach((node) => {
      result += this.serializeNode(node);
    });
    return result;
  }

  serializeNode(node, depth = 0) {
    const nodeSerializer = this.nodes[node.type.name];
    let serializedNode = nodeSerializer ? nodeSerializer(node, this, depth) : '';
    return serializedNode;
  }

  serializeMarks(node) {
    let text = node.text;
    if (node.marks) {
      node.marks.forEach((mark) => {
        const markSerializer = this.marks[mark.type.name];
        if (markSerializer) {
          const hasLeadingSpace = text.startsWith(' ');
          const hasTrailingSpace = text.endsWith(' ');
          text = text.trim();
          let serializedMark = markSerializer(text, mark);
          if (hasLeadingSpace) serializedMark = ' ' + serializedMark;
          if (hasTrailingSpace) serializedMark += ' ';
          text = serializedMark;
        }
      });
    }

    return text;
  }
}

const markdownSerializer = new MarkdownSerializer(
  {
    text: (node, serializer) => serializer.serializeMarks(node),

    paragraph: (node, serializer) => serializer.serializeFragment(node.content) + '\n\n',

    heading: (node, serializer) => {
      const headingContent = serializer.serializeFragment(node.content).trim();
      return '#'.repeat(node.attrs.level) + ' ' + headingContent + '\n\n';
    },

    hardBreak: () => '  \n',

    horizontalRule: () => '---\n\n',

    blockquote: (node, serializer) => {
      const quotedContent = serializer.serializeFragment(node.content).replace(/\n/g, '\n> ');
      return '> ' + quotedContent + '\n\n';
    },

    taskList: (node, serializer, depth = 0) => {
      let result = '';
      node.content.forEach((item, index) => {
        result += serializer.serializeNode(item, depth);
      });
      return result;
    },

    taskListItem: (node, serializer, depth = 0) => {
      const indent = '  '.repeat(depth);
      const checkbox = node.attrs.checked ? '[x]' : '[ ]';
      let content = '';

      node.content.forEach((child, index) => {
        if (child.type.name === 'paragraph') {
          content += serializer.serializeNode(child).trim() + '\n\n';
        } else if (child.type.name === 'taskList') {
          content += serializer.serializeNode(child, depth + 1);
        }
      });

      return `${indent}- ${checkbox} ${content}`;
    },

    bulletList: (node, serializer, depth = 0) => {
      let result = '';
      const indent = ' '.repeat(depth * 4);
      node.content.forEach((item, _, index) => {
        result += indent + '- ' + serializer.serializeNode(item, depth + 1);
        result += index.isLast ? '\n\n' : '\n';
      });
      return result;
    },

    orderedList: (node, serializer, depth = 0) => {
      let result = '';
      const orderStart = node.attrs.order || 1;
      const node_children_count = node.childCount;
      for (let index = 0; index < node_children_count; index++) {
        const item = node.content.child(index);
        const currentOrder = orderStart + index;
        const indent = ' '.repeat(depth * 4);
        result +=
          `${indent}${currentOrder}. ${serializer.serializeNode(item, depth + 1)}` +
          (index === node_children_count - 1 ? '\n\n' : '\n');
      }
      return result;
    },

    listItem: (node, serializer, depth) => {
      let result = '';
      node.content.forEach((childNode) => {
        const serialized_node = serializer.serializeNode(childNode, depth);
        result += serialized_node;
      });

      return result.trimEnd() + '\n';
    },

    codeBlock: (node, serializer) => '```\n' + serializer.serializeFragment(node.content) + '\n```\n\n',

    image: (node) => `![${node.attrs.alt || ''}](${node.attrs.src}${node.attrs.title ? ` "${node.attrs.title}"` : ''})`,

    iframe: (node, serializer) => serializer.serializeIframe(node),

    table: (node, serializer) => serializer.serializeTable(node),

    tableRow: (node, serializer) => serializer.serializeTableRow(node),

    tableCell: (node, serializer) => serializer.serializeTableCell(node),

    tableHeaderCell: (node, serializer) => serializer.serializeTableHeaderCell(node),

    tableControllerCell: () => '',

    mention: (node) => `<span data-mention="${node.attrs.id}">${node.attrs.name || node.attrs.id}</span>`,

    callout: (node, serializer) => {
      const type = node.attrs.type || 'info';
      const content = serializer.serializeFragment(node.content).trim();
      return `:::${type}\n${content}\n:::\n\n`;
    },

    accordionTitle: (node, serializer) => serializer.serializeFragment(node.content).trim(),

    accordionContent: (node, serializer) => serializer.serializeFragment(node.content).trim(),

    accordion: (node, serializer) => {
      const titleNode = node.content?.content?.find((child) => child.type.name === 'accordionTitle');
      const contentNode = node.content?.content?.find((child) => child.type.name === 'accordionContent');

      const title = titleNode ? serializer.serializeFragment(titleNode.content).trim() : 'Details';
      const content = contentNode ? serializer.serializeFragment(contentNode.content).trim() : '';

      return `<details>\n<summary>${title}</summary>\n\n${content}\n\n</details>\n\n`;
    },
  },
  {
    italic: (content) => `*${trim(content)}*`,
    em: (content) => `*${trim(content)}*`,
    bold: (content) => `**${trim(content)}**`,
    strong: (content) => `**${trim(content)}**`,
    code: (content) => `\`${trim(content)}\``,
    strike: (content) => `~~${trim(content)}~~`,
    underline: (content) => `<span style={{'textDecoration': 'underline'}}>${trim(content)}</span>`,
    superscript: (content) => `^${trim(content)}^`,
    subscript: (content) => `~${trim(content)}~`,
    link: (content, mark) =>
      `[${trim(content)}](${mark.attrs.href}${mark.attrs.title ? ` "${mark.attrs.title}"` : ''})`,
    textColor: (content, mark) => {
      if (mark.attrs && mark.attrs.color) {
        return `<span style={{'color': '${mark.attrs.color}'}}>${trim(content)}</span>`;
      }
      return trim(content);
    },
    fontSize: (content, mark) => {
      if (mark.attrs && mark.attrs.size) {
        return `<span style={{'fontSize': '${mark.attrs.size}'}}>${trim(content)}</span>`;
      }
      return trim(content);
    },
  }
);

export { markdownSerializer, MarkdownSerializer };
